/**
 * VeraLok Admin Console — Control Plane (2026-02 UX reconciliation).
 *
 * This is the Admin surface's own dashboard. Prior to this, `/admin`
 * reused the generic `pages/Dashboard.jsx`, which is designed as an
 * institution-facing operations dashboard (marketing-toned copy,
 * bento KPI cards, business-style charts). That created the illusion
 * that Institution Portal and Admin Console were the same product
 * with different sidebars.
 *
 * This control-plane view keeps every backend call the generic
 * dashboard already used (KPIs, audit tail, approvals queue) but
 * composes them as an *operator* would consume them:
 *
 *   • dense grouped queue counters (approval / KYC / CAP / recovery /
 *     suspensions) with direct click-through to the operator page;
 *   • a live event tail (last N audit events, monospace, click-to-open);
 *   • a platform-health strip (backend health, DB, critical events);
 *   • no marketing headline, no big charts, no consumer bento KPIs.
 *
 * Function reuse:
 *   - `GET /dashboard/kpis`   already exposes all the counters we need.
 *   - `GET /audit?limit=…`    already returns the tail.
 *   - `GET /institution/requests?status=PENDING` already returns the
 *     approvals queue (also used by the generic Dashboard).
 * No new backend endpoints are introduced.
 */
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AlertTriangle, Building2, ClipboardList, Fingerprint, HeartPulse, LifeBuoy, Lock, ScrollText, Shield, TerminalSquare } from "lucide-react";

function useKpis() {
  return useQuery({ queryKey: ["admin-cp-kpis"], queryFn: () => api.get("/dashboard/kpis").then(r => r.data) });
}
function useAudit(limit = 15) {
  return useQuery({ queryKey: ["admin-cp-audit", limit], queryFn: () => api.get(`/audit?limit=${limit}`).then(r => r.data) });
}
function useHealth() {
  return useQuery({
    queryKey: ["admin-cp-health"],
    queryFn: () => api.get("/health").then(r => r.data).catch(() => ({ status: "unknown", db: "unknown" })),
    refetchInterval: 30000,
  });
}
function useRuntime() {
  return useQuery({
    queryKey: ["admin-cp-runtime"],
    queryFn: () => api.get("/runtime/mode").then(r => r.data).catch(() => ({ mode: "unknown" })),
    staleTime: 60000,
  });
}

// ─────────────────────────────────────────────────────────────
// QueueTile — dense, monospace, right-aligned count + click-through.
// ─────────────────────────────────────────────────────────────
function QueueTile({ to, icon: Icon, label, value, tone = "default", testId }) {
  const toneClass =
    tone === "danger"  ? "border-red-500/40 hover:border-red-400"     :
    tone === "warning" ? "border-amber-500/40 hover:border-amber-400" :
    "border-slate-700 hover:border-slate-500";
  const numClass =
    tone === "danger"  ? "text-red-300"   :
    tone === "warning" ? "text-amber-300" :
    "text-slate-100";
  return (
    <Link
      to={to}
      className={`block bg-slate-900/40 border ${toneClass} rounded-sm px-4 py-3 transition-colors group`}
      data-testid={testId}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-400 font-mono">
          <Icon className="h-3.5 w-3.5" />
          {label}
        </div>
        <span className={`font-mono text-2xl leading-none ${numClass}`}>
          {value ?? "—"}
        </span>
      </div>
      <div className="mt-1 text-[10px] font-mono text-slate-500 group-hover:text-slate-300 transition-colors">
        open →
      </div>
    </Link>
  );
}

// ─────────────────────────────────────────────────────────────
// EventLine — dense monospace audit tail row.
// ─────────────────────────────────────────────────────────────
function EventLine({ ev }) {
  const risk = (ev.risk_level || "").toLowerCase();
  const riskColor =
    risk === "critical" ? "text-red-400" :
    risk === "high"     ? "text-amber-300" :
    risk === "medium"   ? "text-slate-300" :
    "text-slate-500";
  const ts = ev.timestamp ? new Date(ev.timestamp).toISOString().replace("T", " ").replace("Z", "") : "-";
  return (
    <div className="grid grid-cols-[160px_90px_1fr_120px] items-baseline gap-3 border-b border-slate-800 py-1.5 font-mono text-[11px] hover:bg-slate-900/40 px-2 -mx-2 rounded-sm" data-testid={`admin-cp-event-${ev.event_id || ev.id || ""}`}>
      <span className="text-slate-500">{ts}</span>
      <span className={`uppercase ${riskColor}`}>{ev.risk_level || "-"}</span>
      <span className="text-slate-200 truncate">
        <span className="text-violet-300">{ev.action}</span>
        {" "}
        <span className="text-slate-500">by</span> {ev.actor_email || ev.actor_user_id || "-"}
      </span>
      <span className="text-slate-500 truncate text-right">{ev.target_type || "-"}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Main Control Plane composition
// ─────────────────────────────────────────────────────────────
export default function AdminControlPlane() {
  const { data: kpis } = useKpis();
  const { data: audit } = useAudit(15);
  const { data: health } = useHealth();
  const { data: runtime } = useRuntime();

  const events = audit?.items || audit?.events || [];
  const dbOk = health?.db === "connected";
  const backendOk = health?.status === "healthy";

  return (
    <div className="space-y-6 -mt-2" data-testid="admin-control-plane">
      {/* Header + platform-health strip (monospace, operator-facing) */}
      <div>
        <div className="flex items-baseline gap-3">
          <TerminalSquare className="h-4 w-4 text-amber-300" />
          <h1 className="font-display text-xl tracking-tight text-slate-100">Control plane</h1>
          <span className="text-[11px] font-mono text-slate-500">
            runtime={runtime?.mode || "?"} · env={process.env.NODE_ENV}
          </span>
        </div>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-2 font-mono text-[11px]">
          <HealthPill label="backend" ok={backendOk} testId="admin-cp-health-backend" />
          <HealthPill label="database" ok={dbOk} testId="admin-cp-health-db" />
          <HealthPill label="critical/24h" ok={(kpis?.critical_events_24h || 0) === 0} value={kpis?.critical_events_24h ?? "?"} testId="admin-cp-health-critical" />
          <HealthPill label="pending approvals" ok={(kpis?.pending_approvals || 0) === 0} value={kpis?.pending_approvals ?? "?"} tone="warning" testId="admin-cp-health-approvals" />
        </div>
      </div>

      {/* Operator queues (dense, click-through) */}
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">Operator queues</div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="admin-cp-queues">
          <QueueTile to="/admin/approvals" icon={ClipboardList} label="Institution approvals" value={kpis?.pending_approvals} tone="warning" testId="admin-cp-queue-approvals" />
          <QueueTile to="/admin/kyc-reviews" icon={Fingerprint} label="KYC review"           value={kpis?.pending_kyc_reviews}  testId="admin-cp-queue-kyc" />
          <QueueTile to="/admin/cap" icon={Shield} label="CAP review"                     value={kpis?.pending_cap_reviews}  testId="admin-cp-queue-cap" />
          <QueueTile to="/admin/recovery" icon={LifeBuoy} label="Recovery"                value={kpis?.pending_recovery}     testId="admin-cp-queue-recovery" />
          <QueueTile to="/admin/suspensions" icon={Lock} label="Suspensions"              value={kpis?.suspensions_active}   tone="danger" testId="admin-cp-queue-suspensions" />
          <QueueTile to="/admin/identities" icon={Building2} label="Locked identities"    value={kpis?.identities_locked}    tone="danger" testId="admin-cp-queue-locked" />
        </div>
      </div>

      {/* Live event tail */}
      <div>
        <div className="flex items-baseline justify-between mb-2">
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Live event tail · last 15</div>
          <Link to="/admin/audit" className="text-[11px] font-mono text-violet-300 hover:underline" data-testid="admin-cp-audit-link">
            full audit →
          </Link>
        </div>
        <div className="bg-slate-900/40 border border-slate-800 rounded-sm p-3 min-h-[240px]" data-testid="admin-cp-audit-tail">
          {events.length === 0
            ? <div className="font-mono text-[11px] text-slate-500 py-6 text-center">no recent events</div>
            : events.map((ev, i) => <EventLine key={ev.event_id || ev.id || i} ev={ev} />)
          }
        </div>
      </div>

      {/* Platform totals footer (compact monospace, no charts) */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 font-mono text-[11px]" data-testid="admin-cp-totals">
        <Totals label="identities · active" value={kpis?.identities_active} />
        <Totals label="identities · total"  value={kpis?.identities_total} />
        <Totals label="institutions"        value={kpis?.institutions_total} />
        <Totals label="verifications"       value={kpis?.verifications_total?.toLocaleString?.() ?? kpis?.verifications_total} />
        <Totals label="audit events · 24h"  value={kpis?.audit_events_24h} />
      </div>
    </div>
  );
}

function HealthPill({ label, ok, value, tone, testId }) {
  const cls = ok
    ? "border-emerald-500/40 text-emerald-300"
    : tone === "warning"
      ? "border-amber-500/40 text-amber-300"
      : "border-red-500/40 text-red-300";
  return (
    <div className={`border ${cls} bg-slate-900/40 rounded-sm px-3 py-2 flex items-center justify-between`} data-testid={testId}>
      <span className="text-slate-400">{label}</span>
      <span>{value !== undefined && value !== null ? value : (ok ? "ok" : "check")}</span>
    </div>
  );
}

function Totals({ label, value }) {
  return (
    <div className="border border-slate-800 bg-slate-900/30 rounded-sm px-3 py-2">
      <div className="text-slate-500 text-[10px] uppercase tracking-widest">{label}</div>
      <div className="text-slate-200 text-base font-mono mt-1">{value ?? "—"}</div>
    </div>
  );
}
